import { useMutation, useQueryClient } from '@tanstack/react-query';

import BackendService from '../services/backendService';
import { PromptInputData, PromptTestcase } from '../services/backendService/types';
import { testcasesCacheKey } from './useTestcases';

const useTestcase = ({ presentationId }: { presentationId?: string }) => {
  const client = useQueryClient();
  const { mutate: newTestcase, isLoading: isCreatingNewTestcase } = useMutation(
    ['add-testcase'],
    async ({ inputData }: { inputData: PromptInputData[]; onSuccess?: () => void }) => {
      if (!presentationId) return Promise.resolve(undefined);
      return BackendService.instance.ai.newTestcase({ inputData, presentationId });
    },
    {
      onSuccess: (newTestcase, { onSuccess }) => {
        if (!newTestcase) return;
        client.setQueryData<PromptTestcase[]>(
          [testcasesCacheKey, presentationId],
          (existingTestcases: PromptTestcase[] | undefined) => {
            if (!existingTestcases) return [newTestcase];
            return [...existingTestcases, newTestcase];
          },
        );
        if (onSuccess) onSuccess();
      },
    },
  );
  const { mutate: duplicateTestcase, isLoading: isDuplicating } = useMutation(
    ['duplicate-testcase'],
    async ({ testcaseid }: { testcaseid: string; onSuccess?: () => void }) => {
      if (!presentationId) return Promise.resolve(undefined);
      return BackendService.instance.ai.duplicateTestcase({ testcaseid, presentationId });
    },
    {
      onSuccess: (newTestcase, { onSuccess, testcaseid }) => {
        const existingTestcases = client.getQueryData<PromptTestcase[]>([
          testcasesCacheKey,
          presentationId,
        ]);
        const testcaseDuplicatedFromIndex = existingTestcases?.findIndex(
          e => e.id === testcaseid,
        );

        if (!newTestcase) return;
        client.setQueryData<PromptTestcase[]>(
          [testcasesCacheKey, presentationId],
          (existingTestcases: PromptTestcase[] | undefined) => {
            if (!existingTestcases) return [newTestcase];
            if (testcaseDuplicatedFromIndex !== undefined) {
              const updatedList = [...existingTestcases];
              // insert the newTestcase after the ones that is has been duplicated from
              updatedList.splice(testcaseDuplicatedFromIndex + 1, 0, newTestcase);

              return updatedList;
            }
            return [...existingTestcases, newTestcase];
          },
        );
        if (onSuccess) onSuccess();
      },
    },
  );
  const { mutate: editTestcase, data, isLoading: isEditingNewTestcase } = useMutation(
    ['edit-testcase'],
    async ({
      inputData,
      id,
      isSuggestion,
      isSampleLesson,
      onSuccess,
    }: {
      inputData: PromptInputData[];
      id?: string;
      isSuggestion: boolean;
      isSampleLesson: boolean;
      onSuccess?: () => void;
    }) => {
      if (!presentationId) return Promise.resolve(undefined);
      if (!id) return Promise.resolve(undefined);
      return BackendService.instance.ai.editTestcase({
        inputData,
        presentationId,
        testcaseId: id!,
        isSuggestion,
        isSampleLesson,
      });
    },
    {
      onSuccess: (updatedTestcase, { onSuccess }) => {
        if (!updatedTestcase) return;
        client.setQueryData<PromptTestcase[]>(
          [testcasesCacheKey, presentationId],
          (existingTestcases: PromptTestcase[] | undefined) => {
            if (!existingTestcases) return [];
            return existingTestcases.map(e => {
              if (e.id === updatedTestcase.id) return updatedTestcase;
              return e;
            });
          },
        );
        if (onSuccess) onSuccess();
      },
    },
  );
  const { mutate: deleteTestcase } = useMutation(
    ['delete-testcase'],
    async ({ ids }: { ids?: string[]; onSuccess?: () => void }) => {
      if (!presentationId) return Promise.resolve(undefined);
      if (!ids) return Promise.resolve(undefined);
      return BackendService.instance.ai.deleteTestcase({
        testcaseIds: ids!,
        presentationId,
      });
    },
    {
      onMutate: ({ ids, onSuccess }) => {
        if (!ids) return;
        client.setQueryData<PromptTestcase[]>(
          [testcasesCacheKey, presentationId],
          (existingTestcases: PromptTestcase[] | undefined) => {
            if (!existingTestcases) return [];
            return existingTestcases.filter(e => !ids.includes(e.id));
          },
        );
        if (onSuccess) onSuccess();
      },
    },
  );
  return {
    newTestcase,
    editTestcase,
    duplicateTestcase,
    data,
    deleteTestcase,
    isLoading: isCreatingNewTestcase || isEditingNewTestcase,
  };
};

export default useTestcase;
